import { useEffect, useState } from 'react'

/**
 * Whether the viewport is wide enough for the workflow canvas (D9 of
 * `docs/plans/2026-09-04-workflow-canvas-view.md`). Below this breakpoint
 * `WorkflowStepsCard` mounts `WorkflowStepList` exclusively, so the canvas
 * never has to be responsive down to a phone (D2).
 *
 * Guarded the way `usePrefersReducedMotion` guards `matchMedia`, but the
 * default flips: canvas is the product default (D2a), and `vitest.setup.ts`
 * stubs no `matchMedia`, so an environment that cannot answer the query
 * defaults to wide/canvas rather than silently falling back to the list.
 * The initial state reads `matchMedia` synchronously so a phone does not
 * flash the canvas for one frame before the effect runs.
 */
const WIDE_VIEWPORT_QUERY = '(min-width: 1024px)'

function readIsWideViewport(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return true
  }
  return window.matchMedia(WIDE_VIEWPORT_QUERY).matches
}

export function useIsWideViewport(): boolean {
  const [isWide, setIsWide] = useState(readIsWideViewport)

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return
    }
    const mediaQuery = window.matchMedia(WIDE_VIEWPORT_QUERY)
    setIsWide(mediaQuery.matches)
    const onChange = () => setIsWide(mediaQuery.matches)
    mediaQuery.addEventListener?.('change', onChange)
    return () => mediaQuery.removeEventListener?.('change', onChange)
  }, [])

  return isWide
}
